import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import AppLoader from "../../Common/AppLoader";
import { getUsersApi } from "../../../apiServices/adminWork.services";
import { loginAlterationApi } from "../../../apiServices/adminWork.services";

function LoginModal({ isOpen, toggle, selectedUser, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [employeeStatus, setEmployeeStatus] = useState("active");
  const [leadAction, setLeadAction] = useState("");
  const [reassignedTo, setReassignedTo] = useState(null);
  const [remarks, setRemarks] = useState("");
  const flattenUsers = (list, level = 0) => {
    let arr = [];

    list.forEach((user) => {
      arr.push({
        value: user._id,
        label: `${"— ".repeat(level)}${user.firstName} ${user.lastName}`,
      });

      if (user.reportees?.length) {
        arr = [...arr, ...flattenUsers(user.reportees, level + 1)];
      }
    });

    return arr;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await getUsersApi();
      const records = flattenUsers(res?.data?.users || []);
      setUsers(records);
    } catch (error) {
      toast.error("Unable to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    fetchUsers();

    setEmployeeStatus(selectedUser?.employeeStatus || "active");

    setLeadAction("");
    setReassignedTo(null);
    setRemarks("");
  }, [isOpen, selectedUser]);

  const handleSubmit = async () => {
    if (["resigned", "terminated"].includes(employeeStatus)) {
      if (leadAction === "reassign" && !reassignedTo) {
        return toast.error("Please select a user");
      }
      if (!remarks.trim()) {
        return toast.error("Remarks are required");
      }
    }

    try {
      setLoading(true);
      await loginAlterationApi(selectedUser._id, {
        employeeStatus,
        leadAction,
        reassignedTo: reassignedTo?.value || null,
        remarks,
      });
      toast.success("Employee status updated successfully.");
      toggle();

      onSuccess?.();
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Login Access Management</ModalHeader>

      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <AppLoader />
          </div>
        ) : (
          <>
            <Card className="shadow-sm border-0 mb-4">
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>User</Label>
                      <div className="fw-bold fs-5">
                        {" "}
                        {selectedUser?.firstName} {selectedUser?.lastName}{" "}
                      </div>

                      <div className="text-muted"> {selectedUser?.email} </div>

                      <div className="text-muted">
                        {" "}
                        {selectedUser?.designationId?.title || "-"}{" "}
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="text-md-end">
                    <div className="mb-2">Current Status</div>
                    <span className="badge bg-primary">
                      {selectedUser?.employeeStatus
                        ?.replace("_", " ")
                        ?.toUpperCase() || "ACTIVE"}
                    </span>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <FormGroup>
              <Label className="fw-semibold mb-3"> Employee Status </Label>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setEmployeeStatus("active")}
                  >
                    <label
                      className={`crm-checkbox ${employeeStatus === "active" ? "active" : ""}`}
                    >
                      <span />
                    </label>
                    <span className="ms-2"> Active </span>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setEmployeeStatus("leave")}
                  >
                    <label
                      className={`crm-checkbox ${employeeStatus === "leave" ? "active" : ""}`}
                    >
                      <span />
                    </label>
                    <span className="ms-2"> On Leave </span>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setEmployeeStatus("resigned")}
                  >
                    <label
                      className={`crm-checkbox ${employeeStatus === "resigned" ? "active" : ""}`}
                    >
                      <span />
                    </label>
                    <span className="ms-2"> Resigned </span>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setEmployeeStatus("terminated")}
                  >
                    <label
                      className={`crm-checkbox ${employeeStatus === "terminated" ? "active" : ""}`}
                    >
                      <span />
                    </label>
                    <span className="ms-2"> Terminated </span>
                  </div>
                </div>

              </div>
            </FormGroup>
            <div className="alert alert-light border mt-3">
              {employeeStatus === "active" && (
                <>
                  <b>Active</b>

                  <ul className="mb-0 mt-2">
                    <li>Login Allowed</li>
                    <li>Existing leads remain assigned</li>
                    <li>Upcoming leads will continue</li>
                  </ul>
                </>
              )}

              {employeeStatus === "leave" && (
                <>
                  <b>On Leave</b>

                  <ul className="mb-0 mt-2">
                    <li>Login Allowed</li>
                    <li>Existing leads remain assigned</li>
                    <li>Upcoming leads will stop</li>
                  </ul>
                </>
              )}


              {["resigned", "terminated"].includes(employeeStatus) && (
                <>
                  <b>
                    {employeeStatus === "resigned" ? "Resigned" : "Terminated"}
                  </b>

                  <ul className="mb-0 mt-2">
                    <li>Login Blocked</li>
                    <li>Choose existing lead handling below</li>
                    <li>Upcoming leads will stop</li>
                  </ul>
                </>
              )}
            </div>
            {["resigned", "terminated"].includes(employeeStatus) && (
              <>
                <hr />

                <FormGroup>
                  <Label> Lead Handling </Label>
                  <div className="mt-2">
                    <div
                      className="d-flex align-items-center mb-3"
                      style={{ cursor: "pointer" }}
                      onClick={() => setLeadAction("reassign")}
                    >
                      <label
                        className={`crm-checkbox ${leadAction === "reassign" ? "active" : ""}`}
                      >
                        <span />
                      </label>
                      <span className="ms-2">
                        Reassign all leads to another user
                      </span>
                    </div>

                    {leadAction === "reassign" && (
                      <Select
                        value={reassignedTo}
                        options={users.filter(
                          (u) => u.value !== selectedUser?._id,
                        )}
                        onChange={setReassignedTo}
                        placeholder="Select User"
                      />
                    )}

                    <div
                      className="d-flex align-items-center mt-4"
                      style={{ cursor: "pointer" }}
                      onClick={() => setLeadAction("unassign")}
                    >
                      <label
                        className={`crm-checkbox ${leadAction === "unassign" ? "active" : ""}`}
                      >
                        <span />
                      </label>

                      <span className="ms-2">
                        Make all assigned leads unassigned
                      </span>
                    </div>
                  </div>
                </FormGroup>

                <FormGroup className="mt-4">
                  <Label>Remarks</Label>

                  <Input
                    type="textarea"
                    rows="4"
                    value={remarks}
                    placeholder="Reason for login blockage..."
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </FormGroup>
              </>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={loading}>
          Cancel
        </Button>

        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          <i className="mdi mdi-content-save me-1" />
          Save Changes
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default LoginModal;
